(() => {
  if (customElements.get('t26-product-recommendations')) return;

  class T26ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.t26Observer || this.t26Controller || this.t26Finished) return;
      if (!(window.SearchAndFilterEmbed?.version === 1 && window.SearchAndFilterEmbed.recommendations === true)) {
        this.t26Finished = true;
        this.dataset.t26RecommendationState = 'fallback';
        return;
      }

      this.dataset.t26RecommendationState = 'waiting';
      if (typeof IntersectionObserver === 'undefined') {
        this.t26Load();
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (this.t26Observer === observer && entries.some((entry) => entry.isIntersecting)) {
          this.t26Load();
        }
      }, { rootMargin: '1500px' });
      this.t26Observer = observer;
      observer.observe(this);
    }

    disconnectedCallback() {
      this.t26Observer?.disconnect();
      this.t26Observer = null;
      this.t26Controller?.abort();
      this.t26Controller = null;
      clearTimeout(this.t26Timeout);
    }

    async t26Load() {
      if (!this.isConnected || this.t26Controller || this.t26Finished) return;
      this.t26Observer?.disconnect();
      this.t26Observer = null;
      const controller = new AbortController();
      this.t26Controller = controller;
      this.dataset.t26RecommendationState = 'loading';
      const active = () => this.isConnected && this.t26Controller === controller && !controller.signal.aborted;
      const finish = (state) => {
        clearTimeout(this.t26Timeout);
        this.t26Controller = null;
        this.t26Finished = true;
        this.dataset.t26RecommendationState = state;
      };
      // One deadline includes both the public API and all Shopify card requests.
      this.t26Timeout = setTimeout(() => {
        if (!active()) return;
        controller.abort();
        finish('fallback');
      }, 10000);

      try {
        const { t26ProductHandle: sourceHandle, t26SectionId: sectionId } = this.dataset;
        const shell = this.querySelector('template[data-t26-recommendation-shell]');
        const limit = Math.min(8, Math.max(1, Number.parseInt(this.dataset.t26Limit, 10) || 8));
        const root = new URL(this.dataset.t26RootUrl || '/', window.location.origin);
        if (!sourceHandle || !sectionId || !shell || root.origin !== window.location.origin) {
          throw new Error('Invalid recommendation context');
        }

        const apiUrl = new URL('https://search-filter.thetex.co.kr/api/storefront/recommendations');
        apiUrl.searchParams.set('product_handle', sourceHandle);
        apiUrl.searchParams.set('limit', String(limit));
        const response = await fetch(apiUrl.href, { credentials: 'omit', signal: controller.signal });
        if (!active()) return;
        if (!response.ok) throw new Error('Recommendation API failed');
        const payload = await response.json();
        if (!active()) return;
        if (payload?.ok !== true || payload.enabled !== true || !Array.isArray(payload.products)) {
          throw new Error('Recommendations unavailable');
        }

        const handles = [];
        const seen = new Set([sourceHandle]);
        for (const product of payload.products) {
          const handle = typeof product?.handle === 'string' ? product.handle.trim() : '';
          if (!/^[a-z0-9][a-z0-9-]*$/.test(handle) || seen.has(handle)) continue;
          seen.add(handle);
          handles.push(handle);
          if (handles.length === limit) break;
        }
        if (!handles.length) throw new Error('No recommendation handles');

        const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
        const results = await Promise.allSettled(handles.map(async (handle) => {
          const cardUrl = new URL(root.origin);
          cardUrl.pathname = `${root.pathname.replace(/\/$/, '')}/products/${handle}`;
          cardUrl.searchParams.set('section_id', sectionId);
          if (previewTheme) cardUrl.searchParams.set('preview_theme_id', previewTheme);
          const cardResponse = await fetch(cardUrl.href, { credentials: 'same-origin', signal: controller.signal });
          if (!active() || !cardResponse.ok) throw new Error('Recommendation card unavailable');
          const html = await cardResponse.text();
          if (!active()) throw new Error('Recommendation request ended');
          const parsed = document.createElement('template');
          parsed.innerHTML = html;
          const cardTemplate = Array.from(parsed.content.querySelectorAll('template[data-t26-recommendation-card]'))
            .find((template) => template.getAttribute('data-t26-recommendation-card') === handle);
          const card = cardTemplate?.content.querySelector('product-card');
          if (!card) throw new Error('Missing recommendation card');
          const clone = card.cloneNode(true);
          // Template parsing is inert; remove scripts before adopting cloned cards.
          clone.querySelectorAll('script').forEach((script) => script.remove());
          return clone;
        }));
        if (!active()) return;

        // Keep the shell in its inert template document until every item exists.
        // Native carousel and card constructors must see populated children.
        const layout = shell.content.cloneNode(true);
        const items = layout.querySelector('[data-t26-recommendation-items]');
        const cards = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
        if (!items || !cards.length) throw new Error('No valid recommendation cards');
        cards.forEach((card) => {
          const item = layout.ownerDocument.createElement('li');
          if (items.classList.contains('slider__grid')) item.className = 'slider__item';
          item.append(card);
          items.append(item);
        });
        this.replaceChildren(layout);
        finish('app');
      } catch (error) {
        if (active()) finish('fallback');
      }
    }
  }

  customElements.define('t26-product-recommendations', T26ProductRecommendations);
})();
